/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Sizes } from '@backmarket/design-system/components/CardCarousel/CardCarousel.constant'
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { z } from 'zod/mini'

import { createBlockSchema } from '../schemas/create-block-schema'
import { type HeadingSize, HeadingSizeSchema } from '../shared/heading-size'
import type { LoosenRichTextFields } from '../types/loose-rich-text'
import type { Equal, ExpectTrue } from '../types/validation'

import { type ApiBackground, ApiBackgroundSchema } from './background'
import type { _ApiBlock } from './block'
import { ButtonsApiPropsSchema } from './buttons'
import { CmsButtonApiPropsSchema } from './cms-button'
import { type ContentLink, ContentLinkSchema } from './content-link'
import { type CTALink, CTALinkSchema } from './cta-link'
import { type Image, ImageSchema } from './image'
import { ListItemSchema } from './list-item'
import { MediaApiPropsSchema } from './media'
import { RichTextApiPropsSchema } from './rich-text-content'

/* TypeScript */
type ModularCardLayout = 'grid' | 'carousel'

export type ModularCardsCollectionApiProps = {
  technicalName?: Maybe<string>
  title?: Maybe<string>
  subtitle?: Maybe<string>
  headingSize?: Maybe<HeadingSize>
  size: Sizes
  cards: ModularCard[]
  cardsHavePadding?: Maybe<boolean>
  // "layout" is for the mobile layout only, the name cannot be changed due to retro compatibility reasons.
  layout?: Maybe<ModularCardLayout>
  desktopLayout?: Maybe<ModularCardLayout>
  mobileScreenColumns?: Maybe<1 | 2>
  ctaLink?: Maybe<CTALink>
  fitToScreen?: Maybe<boolean>
}

export type ModularCard = {
  technicalName?: Maybe<string>
  id: string
  sections: ModularCardSection[]
  size?: Maybe<Sizes>
  background?: Maybe<ApiBackground>
  link?: Maybe<ContentLink>
}

export type ModularCardSection = {
  alignment?: Maybe<ModularCardSectionAlignment>
  slots: Array<
    | _ApiBlock<'Button'>
    | _ApiBlock<'Buttons'>
    | _ApiBlock<'Media'>
    | _ApiBlock<'ModularCardImage'>
    | _ApiBlock<'ListItem'>
    | _ApiBlock<'RichText'>
  >
}

export type ModularCardImageApiProps = {
  technicalName?: Maybe<string>
  image: Image
  link?: Maybe<ContentLink>
  rounded?: Maybe<boolean>
}

export type ModularCardSectionAlignment = 'center' | 'left'

/* Schemas */
export const ModularCardSizeSchema = z.enum([
  'extra-small',
  'small',
  'medium',
  'large',
  'extra-large',
])
export const ModularCardSectionAlignmentSchema = z.enum(['center', 'left'])
export const ModularCardLayoutSchema = z.enum(['grid', 'carousel'])

export const ModularCardImageApiPropsSchema = z.object({
  technicalName: z.nullish(z.string()),
  image: ImageSchema,
  link: z.nullish(ContentLinkSchema),
  rounded: z.nullish(z.boolean()),
})

export const ModularCardSectionSchema = z.object({
  alignment: z.nullish(ModularCardSectionAlignmentSchema),
  slots: z.array(
    z.union([
      createBlockSchema('Button', CmsButtonApiPropsSchema),
      createBlockSchema('Buttons', ButtonsApiPropsSchema),
      createBlockSchema('Media', MediaApiPropsSchema),
      createBlockSchema('ModularCardImage', ModularCardImageApiPropsSchema),
      createBlockSchema('ListItem', ListItemSchema),
      createBlockSchema('RichText', RichTextApiPropsSchema),
    ]),
  ),
})

export const ModularCardSchema = z.object({
  technicalName: z.nullish(z.string()),
  id: z.string(),
  sections: z.array(ModularCardSectionSchema),
  size: z.nullish(ModularCardSizeSchema),
  background: z.nullish(ApiBackgroundSchema),
  link: z.nullish(ContentLinkSchema),
})

export const ModularCardsCollectionApiPropsSchema = z.object({
  technicalName: z.nullish(z.string()),
  title: z.nullish(z.string()),
  subtitle: z.nullish(z.string()),
  headingSize: z.nullish(HeadingSizeSchema),
  size: ModularCardSizeSchema,
  cards: z.array(ModularCardSchema),
  cardsHavePadding: z.nullish(z.boolean()),
  layout: z.nullish(ModularCardLayoutSchema),
  desktopLayout: z.nullish(ModularCardLayoutSchema),
  mobileScreenColumns: z.nullish(z.union([z.literal(1), z.literal(2)])),
  ctaLink: z.nullish(CTALinkSchema),
  fitToScreen: z.nullish(z.boolean()),
})

/* Schemas validation */
type TestModularCardsCarouselProps = ExpectTrue<
  Equal<
    LoosenRichTextFields<ModularCardsCollectionApiProps>,
    z.infer<typeof ModularCardsCollectionApiPropsSchema>
  >
>
